import { request } from '../../api';

export interface CommsTemplate {
  readonly id: string;
  readonly code: string;
  readonly version: number;
  readonly kind: 'service' | 'marketing';
  readonly channel: 'sms' | 'whatsapp' | 'push' | 'email' | 'auto';
  readonly locale: 'ru' | 'kk';
  readonly subject: string | null;
  readonly body: string;
  readonly variables: readonly string[];
  readonly status: 'draft' | 'active' | 'archived';
}

export function listTemplates(): Promise<{ templates: readonly CommsTemplate[] }> {
  return request('GET', '/api/v1/comms/templates');
}

export interface TemplateVariable {
  readonly name: string;
  readonly titleRu: string;
  readonly titleKk: string;
  readonly exampleRu: string;
  readonly exampleKk: string;
}

/**
 * Назначение шаблона: что за сообщение и когда уходит.
 *
 * Справочник приходит с сервера, а не живёт копией в интерфейсе: набор
 * назначений задаёт платформа, и разойтись эти два списка не должны.
 */
export interface TemplatePurpose {
  readonly code: string;
  readonly titleRu: string;
  readonly titleKk: string;
  readonly whenRu: string;
  readonly whenKk: string;
  readonly kind: 'service' | 'marketing';
  readonly event: string | null;
  readonly variables: readonly TemplateVariable[];
}

export function templateCatalog(): Promise<{ purposes: readonly TemplatePurpose[] }> {
  return request('GET', '/api/v1/comms/templates/catalog');
}

export interface SaveTemplateInput {
  readonly code: string;
  /** Вид задаёт справочник назначений; сервер расхождение отклонит. */
  readonly kind: 'service' | 'marketing';
  readonly channel: 'sms' | 'whatsapp' | 'push' | 'email' | 'auto';
  readonly locale: 'ru' | 'kk';
  readonly body: string;
  readonly variables: readonly string[];
}

export function saveTemplate(input: SaveTemplateInput): Promise<CommsTemplate> {
  return request('POST', '/api/v1/comms/templates', input);
}

export interface MessageLogItem {
  readonly id: string;
  readonly customerId: string | null;
  readonly customerName: string | null;
  /** Сотрудник-получатель служебного уведомления: эскалации, оповещения. */
  readonly employeeName?: string | null;
  readonly recipientMasked: string;
  readonly channel: string;
  readonly templateCode: string | null;
  readonly templateKind: string;
  readonly status: string;
  readonly reason: string;
  readonly consentBasis: string;
  readonly consentId: string | null;
  readonly suppressedReason: string | null;
  readonly provider: string | null;
  readonly attempts: number;
  readonly createdAt: string;
  readonly sentAt: string | null;
}

export function messageLog(filters: {
  customerId?: string;
  status?: string;
}): Promise<{ items: readonly MessageLogItem[] }> {
  const p = new URLSearchParams();
  if (filters.customerId) p.set('customer_id', filters.customerId);
  if (filters.status) p.set('status', filters.status);
  const qs = p.toString();
  return request('GET', `/api/v1/comms/log${qs ? `?${qs}` : ''}`);
}

export interface ProviderHealth {
  readonly channel: string;
  readonly name: string;
  readonly ok: boolean;
  readonly detail: string;
}

export function providersHealth(): Promise<{ providers: readonly ProviderHealth[] }> {
  return request('GET', '/api/v1/comms/providers/health');
}

export interface BroadcastResult {
  readonly broadcastId: string;
  readonly requested: number;
  readonly queued: number;
  readonly suppressed: number;
  readonly segmentMode: 'live' | 'scheduled';
  readonly suppressedByReason: Readonly<Record<string, number>>;
}

export function broadcast(input: {
  segmentId: string;
  template: string;
  variables: Record<string, unknown>;
  reason: string;
}): Promise<BroadcastResult> {
  return request('POST', '/api/v1/comms/broadcast', input);
}

/**
 * Отправка одному клиенту.
 *
 * Маршрут был с самого начала — им пользуются, когда надо ответить конкретному
 * человеку вне обращения, — а в интерфейсе была только массовая рассылка по
 * сегменту. Согласие проверяет сервер: маркетинговый шаблон уйдёт только тому,
 * кто на него подписан, и отказ вернётся причиной, а не молчанием.
 */
export interface SendResult {
  readonly id: string;
  readonly status: 'queued' | 'suppressed';
  readonly channel: string;
  readonly templateKind: 'service' | 'marketing';
  readonly consentBasis: string;
  readonly suppressedReason: string | null;
}

export function sendOne(input: {
  customerId: string;
  template: string;
  variables: Record<string, unknown>;
  reason: string;
}): Promise<SendResult> {
  return request('POST', '/api/v1/comms/send', input);
}

export interface SegmentOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly lastSize: number | null;
}

export type TriggerChannel = 'auto' | 'origin' | 'sms' | 'whatsapp' | 'push' | 'email';
export type TriggerStatus = 'active' | 'paused' | 'archived';
export type RunStatus = 'scheduled' | 'sent' | 'suppressed' | 'skipped' | 'failed' | 'cancelled';

export type TriggerAudience =
  | { readonly type: 'customer' }
  | { readonly type: 'employee'; readonly field: string }
  | { readonly type: 'role'; readonly role: string };

export interface PayloadCondition {
  readonly field: string;
  readonly op: 'eq' | 'neq' | 'in' | 'nin' | 'gt' | 'gte' | 'lt' | 'lte' | 'exists' | 'missing';
  readonly value?: string | number | boolean;
}

export interface TriggerConditions {
  readonly payload: readonly PayloadCondition[];
  readonly sites: readonly string[];
  readonly noVisitSince: boolean;
  readonly subscriptionStatusIn: readonly string[];
}

export interface TriggerInput {
  readonly name: string;
  readonly eventKind: string;
  readonly templateCode: string;
  readonly channel: TriggerChannel;
  readonly locale: 'ru' | 'kk';
  readonly audience: TriggerAudience;
  readonly conditions: TriggerConditions;
  readonly delayMinutes: number;
  readonly anchorField: string | null;
  readonly cooldownMinutes: number;
  readonly quietHours: boolean;
  readonly status: TriggerStatus;
}

export interface Trigger extends TriggerInput {
  readonly id: string;
  readonly runs30d: Readonly<Partial<Record<RunStatus, number>>>;
}

export interface EventField {
  readonly name: string;
  readonly titleRu: string;
  readonly titleKk: string;
}

export interface TriggerEvent {
  readonly kind: string;
  readonly spec: number;
  readonly titleRu: string;
  readonly titleKk: string;
  readonly fields: readonly EventField[];
  readonly hasCustomer: boolean;
}

export interface TriggerRun {
  readonly id: string;
  readonly triggerId: string;
  readonly eventKind: string;
  readonly recipientName: string | null;
  readonly status: RunStatus;
  readonly reason: string | null;
  readonly fireAt: string;
  readonly processedAt: string | null;
}

export function listTriggers(): Promise<{ items: readonly Trigger[] }> {
  return request('GET', '/api/v1/comms/triggers');
}

export function triggerEvents(): Promise<{ events: readonly TriggerEvent[] }> {
  return request('GET', '/api/v1/comms/triggers/events');
}

export function createTrigger(input: TriggerInput): Promise<Trigger> {
  return request('POST', '/api/v1/comms/triggers', input);
}

export function updateTrigger(id: string, input: TriggerInput): Promise<Trigger> {
  return request('PUT', `/api/v1/comms/triggers/${encodeURIComponent(id)}`, input);
}

export function triggerRuns(triggerId: string): Promise<{ items: readonly TriggerRun[] }> {
  return request('GET', `/api/v1/comms/triggers/runs?trigger_id=${encodeURIComponent(triggerId)}&limit=50`);
}

export interface ProvidersState {
  readonly providers: readonly (ProviderHealth & { readonly webhook?: boolean })[];
  readonly inbound?: Readonly<Record<string, number>>;
  readonly alertmanager?: boolean;
}

export function providersState(): Promise<ProvidersState> {
  return request('GET', '/api/v1/comms/providers/health');
}

export function listSegments(): Promise<{ segments: readonly SegmentOption[] }> {
  return request('GET', '/api/v1/identity/segments');
}
